import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import {
    ArrayPath,
    Controller,
    FieldValues,
    UseFieldArrayAppend
} from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";
import { getFieldValue } from "@/components/Form/RHF/Utils/form.helpers";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { MultiTextFieldChildrenProps } from "./multiTextField.component";

type TextFieldArrayProps<T extends FieldValues> =
    MultiTextFieldChildrenProps<T> & {
        placeholder?: string;
        maxFields?: number;
        addFieldLabel?: string;
        onAdd: (append: UseFieldArrayAppend<T, ArrayPath<T>>) => void;
    };

const TextFieldArray = <T extends FieldValues>({
    fields,
    control,
    name,
    actions,
    disabled,
    placeholder,
    maxFields,
    addFieldLabel,
    onAdd
}: TextFieldArrayProps<T>) => {
    const { t } = useGranite();

    const reachedMaxFields =
        maxFields !== undefined && fields.length >= maxFields;

    const handleOnRemove = (index: number) => () => {
        actions.remove(index);
    };

    const handleOnAdd = () => {
        onAdd(actions.append);
    };

    return (
        <>
            {fields.map((fieldItem, index) => (
                <div
                    key={fieldItem.id}
                    className={cn("tw:flex tw:align-items-center tw:mb-1")}
                >
                    <Controller<T>
                        control={control}
                        name={`${name}.${index}.value`}
                        render={({ field, fieldState, formState }) => (
                            <Input
                                {...field}
                                value={getFieldValue(
                                    field,
                                    formState.isLoading,
                                    ""
                                )}
                                className={cn("tw:grow tw:mr-2")}
                                type="text"
                                inputMode="text"
                                id={`${name}.${index}`}
                                placeholder={placeholder}
                                valid={
                                    fieldState.isDirty && !fieldState.invalid
                                }
                                invalid={fieldState.invalid}
                                disabled={disabled}
                            />
                        )}
                    />
                    <Button
                        className={cn("tw:inline-block tw:ml-2")}
                        size="sm"
                        disabled={disabled}
                        onClick={handleOnRemove(index)}
                    >
                        <i className={cn("las la-trash")} />
                    </Button>
                </div>
            ))}
            {!reachedMaxFields && (
                <Button
                    className={cn("tw:mt-2")}
                    size={"sm"}
                    color={"primary"}
                    outline={true}
                    disabled={disabled}
                    onClick={handleOnAdd}
                >
                    {addFieldLabel ||
                        t("components.form.fields.multiTextField.addField")}
                </Button>
            )}
        </>
    );
};

export { TextFieldArray };
